import {withReactQuery} from '@salesforce/pwa-kit-react-sdk/ssr/universal/components/with-react-query'
import {QueryClient} from '@tanstack/react-query'

export const queryClientConfig = {
    defaultOptions: {
        queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 0
        }
    }
}

const PERFORMANCE_MARKS = {
    reactQueryPrerender: 'reactQueryPrerender'
}

/**
 * Drop-in replacement for withReactQuery that supports dependent queries in SSR.
 * It reruns doInitAppState multiple times until no new queries are discovered.
 */
export const withReactQueryMultipass = (Wrapped, options = {}) => {
    const WithRQ = withReactQuery(Wrapped, options)

    class WithReactQueryMultipass extends WithRQ {
        static async doInitAppState({res, appJSX, maxPasses = 7}) {
            let state = {}
            let previousHashes = new Set()

            // Wrap JSX into a factory so we can rebuild it fresh every pass
            const appJSXFactory = typeof appJSX === 'function' ? appJSX : () => appJSX

            for (let pass = 0; pass < maxPasses; pass++) {
                const passMark = `${PERFORMANCE_MARKS.reactQueryPrerender}.pass${pass + 1}`
                res.__performanceTimer?.mark(passMark, 'start')

                // Always rebuild a fresh JSX tree
                const freshAppJSX = appJSXFactory()

                state = await super.doInitAppState({
                    res,
                    appJSX: freshAppJSX
                })

                res.__performanceTimer?.mark(passMark, 'end')

                // Grab queries currently in the cache
                const queryClient = res.locals?.__queryClient || new QueryClient(queryClientConfig)

                const queryCache = queryClient.getQueryCache()
                const hashes = new Set(
                    queryCache
                        .getAll()
                        .filter((q) => q.options.enabled !== false)
                        .map((q) => q.queryHash)
                )

                // If no new queries were added, stop early
                if (
                    hashes.size === previousHashes.size &&
                    [...hashes].every((h) => previousHashes.has(h))
                ) {
                    break
                }

                previousHashes = hashes
            }

            return state
        }

        static getInitializers() {
            const rest = super.getInitializers?.() ?? []
            return [
                WithReactQueryMultipass.doInitAppState,
                ...rest.filter((fn) => fn !== super.doInitAppState)
            ]
        }

        static getHOCsInUse() {
            return [withReactQueryMultipass, ...(super.getHOCsInUse?.() ?? [])]
        }
    }

    WithReactQueryMultipass.displayName = `withReactQueryMultipass(${
        Wrapped.displayName || Wrapped.name
    })`

    return WithReactQueryMultipass
}
